<template>
  <ul
    v-if="$canNavCapability('nav.tables_section', workspace.id)"
    class="tree sidebar__solution-tree"
  >
    <SidebarNavGroup
      v-for="group in solutionGroups"
      :key="group.id"
      :title="group.label"
      :icon="group.icon || 'iconoir-view-grid'"
      :count="group.items.length"
    >
      <ul class="tree__subs">
        <nuxt-link
          v-for="item in group.items"
          :key="item.key"
          v-slot="{ href, navigate, isActive }"
          :to="item.route"
        >
          <li class="tree__sub" :class="{ active: isActive }">
            <a :href="href" class="tree__sub-link" @click="navigate">
              <i class="tree__sub-icon" :class="item.icon"></i>
              <span class="tree__sub-name">{{ item.label }}</span>
            </a>
          </li>
        </nuxt-link>
      </ul>
    </SidebarNavGroup>

    <SidebarNavGroup
      v-if="standaloneEntries.length"
      :title="$t('sidebar.tables')"
      icon="iconoir-view-grid"
      :count="standaloneEntries.length"
    >
      <ul class="tree__subs">
        <nuxt-link
          v-for="entry in standaloneEntries"
          :key="entry.id"
          v-slot="{ href, navigate, isActive }"
          :to="tableRoute(entry)"
        >
          <li class="tree__sub" :class="{ active: isActive }">
            <a :href="href" class="tree__sub-link" @click="navigate">
              <span class="tree__sub-name">{{ entry.label }}</span>
            </a>
          </li>
        </nuxt-link>
      </ul>
    </SidebarNavGroup>
  </ul>
</template>

<script>
import { notifyIf } from '@baserow/modules/core/utils/error'
import WorkspaceMenuEntriesService from '@baserow/modules/core/services/workspaceMenuEntries'
import SidebarNavGroup from '@baserow/modules/core/components/sidebar/SidebarNavGroup'

const OPERATIONAL_VIEW_ICONS = {
  kanban: 'iconoir-kanban-board',
  calendar: 'iconoir-calendar',
  timeline: 'iconoir-clock',
}

const HOSPITALITY_SETUP_ONLY_TABLES = new Set([
  'Habitaciones',
  'Tipos de cargo',
  'Equipo',
])

// Icons that were shipped in templates but do not exist in our iconoir set.
const SIDEBAR_ICON_ALIASES = {
  'iconoir-hotel': 'iconoir-bed',
  'iconoir-cleaning-bucket': 'iconoir-wash',
}

function resolveSidebarIcon(icon, fallback = 'iconoir-view-grid') {
  const raw = icon || fallback
  return SIDEBAR_ICON_ALIASES[raw] || raw
}

export default {
  name: 'SidebarTablesMenu',
  components: { SidebarNavGroup },
  props: {
    workspace: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      entries: [],
    }
  },
  computed: {
    visibleEntries() {
      return this.entries.filter((entry) => {
        if (entry.settings?.operator_only) {
          if (
            !this.$canNavCapability('nav.settings_roles', this.workspace.id)
          ) {
            return false
          }
        }
        if (
          entry.settings?.solution?.id === 'hospitality_ops' &&
          HOSPITALITY_SETUP_ONLY_TABLES.has(entry.label)
        ) {
          return false
        }
        return this.$canNavCapability(
          `table_menu_entry:${entry.id}`,
          this.workspace.id
        )
      })
    },
    standaloneEntries() {
      return this.visibleEntries.filter((entry) => !this.solution(entry))
    },
    solutionGroups() {
      const groups = []
      const groupsById = {}

      this.visibleEntries
        .filter((entry) => this.solution(entry))
        .forEach((entry) => {
          const solution = this.solution(entry)
          if (!groupsById[solution.id]) {
            groupsById[solution.id] = {
              id: solution.id,
              label: solution.label,
              icon: resolveSidebarIcon(solution.icon),
              items: [],
            }
            groups.push(groupsById[solution.id])
          }

          this.operationalViews(entry).forEach((view) => {
            groupsById[solution.id].items.push({
              key: `${entry.id}:${view.id}`,
              label: view.label,
              icon: OPERATIONAL_VIEW_ICONS[view.type] || 'iconoir-view-grid',
              route: this.operationalViewRoute(entry, view),
            })
          })

          groupsById[solution.id].items.push({
            key: `${entry.id}:table`,
            label: entry.label,
            icon: resolveSidebarIcon(entry.icon, 'iconoir-table'),
            route: this.tableRoute(entry),
          })
        })

      return groups
        .filter((group) => group.items.length > 0)
        .map((group) => this.withSolutionHomeLink(group))
    },
    gestoriaHomeRoute() {
      return {
        name: 'workspace-gestoria-home',
        params: {
          workspaceId: this.workspace.id,
        },
      }
    },
    hospitalityHomeRoute() {
      return {
        name: 'workspace-hospitality-home',
        params: {
          workspaceId: this.workspace.id,
        },
      }
    },
    hospitalityAccessRoute() {
      // Locks live inside Configura tu hotel so sidebar stays on setup.
      return `/workspace/${this.workspace.id}/hospitality/setup?tab=locks`
    },
    hospitalityPlanningRoute() {
      return `/workspace/${this.workspace.id}/hospitality/planning`
    },
    hospitalitySetupRoute() {
      return `/workspace/${this.workspace.id}/hospitality/setup`
    },
  },
  watch: {
    workspace: {
      immediate: true,
      handler() {
        this.loadEntries()
      },
    },
  },
  mounted() {
    window.addEventListener(
      'workspace-menu-entries-updated',
      this.handleEntriesUpdated
    )
  },
  beforeUnmount() {
    window.removeEventListener(
      'workspace-menu-entries-updated',
      this.handleEntriesUpdated
    )
  },
  methods: {
    hasNamedRoute(name) {
      try {
        return Boolean(this.$router?.hasRoute?.(name))
      } catch (e) {
        return false
      }
    },
    withSolutionHomeLink(group) {
      if (group.id === 'gestoria_advisory') {
        return this.prependHomeLink(group, {
          key: 'gestoria-home',
          label: this.$t('sidebar.gestoriaHome'),
          icon: 'iconoir-home-simple',
          route: this.gestoriaHomeRoute,
        })
      }
      if (group.id === 'hospitality_ops') {
        // Prepend links: Inicio → Planning → Configura tu hotel → tables.
        let next = group
        if (this.hasNamedRoute('workspace-hospitality-setup')) {
          next = this.prependHomeLink(next, {
            key: 'hospitality-setup',
            label: this.$t('sidebar.hospitalitySetup'),
            icon: 'iconoir-settings',
            route: this.hospitalitySetupRoute,
          })
        }
        next = this.prependHomeLink(next, {
          key: 'hospitality-planning',
          label: this.$t('sidebar.hospitalityPlanning'),
          icon: 'iconoir-calendar',
          route: this.hospitalityPlanningRoute,
        })
        return this.prependHomeLink(next, {
          key: 'hospitality-home',
          label: this.$t('sidebar.hospitalityHome'),
          icon: 'iconoir-home-simple',
          route: this.hospitalityHomeRoute,
        })
      }
      return group
    },
    prependHomeLink(group, homeItem) {
      if (group.items.some((item) => item.key === homeItem.key)) {
        return group
      }
      return {
        ...group,
        items: [homeItem, ...group.items],
      }
    },
    withGestoriaHomeLink(group) {
      return this.withSolutionHomeLink(group)
    },
    solution(entry) {
      const solution = entry.settings?.solution
      if (!solution?.id || !solution?.label) {
        return null
      }

      return solution
    },
    tableRoute(entry) {
      return {
        name: 'workspace-crm-table',
        params: {
          workspaceId: this.workspace.id,
          databaseId: entry.database_id,
          tableId: entry.target_id,
        },
      }
    },
    operationalViews(entry) {
      return (entry.settings?.operational_views || []).filter((view) =>
        ['kanban', 'calendar', 'timeline'].includes(view.type)
      )
    },
    operationalViewRoute(entry, view) {
      if (view.type === 'calendar') {
        return {
          name: 'workspace-template-calendar',
          params: {
            workspaceId: this.workspace.id,
            tableId: entry.target_id,
          },
          query: {
            dateFieldId: view.date_field_id,
            cardFieldIds: (view.card_field_ids || []).join(','),
            filterFieldIds: (view.filter_field_ids || []).join(','),
            mode: view.mode || 'month',
            label: view.label,
          },
        }
      }

      if (view.type === 'timeline') {
        return {
          name: 'workspace-template-timeline',
          params: {
            workspaceId: this.workspace.id,
            tableId: entry.target_id,
          },
          query: {
            dateFieldId: view.date_field_id,
            cardFieldIds: (view.card_field_ids || []).join(','),
            detailFieldIds: (view.detail_field_ids || []).join(','),
            filterFieldIds: (view.filter_field_ids || []).join(','),
            datePreset: view.date_preset || 'upcoming',
            descending: view.descending ? 'true' : 'false',
            label: view.label,
          },
        }
      }

      return {
        name: 'workspace-template-kanban',
        params: {
          workspaceId: this.workspace.id,
          tableId: entry.target_id,
        },
        query: {
          groupFieldId: view.group_field_id,
          cardFieldIds: (view.card_field_ids || []).join(','),
          label: view.label,
        },
      }
    },
    handleEntriesUpdated(event) {
      if (event?.detail?.workspaceId !== this.workspace?.id) {
        return
      }
      this.loadEntries()
    },
    async loadEntries() {
      if (!this.workspace?.id) {
        this.entries = []
        return
      }

      try {
        const service = WorkspaceMenuEntriesService(this.$client)
        const { data } = await service.fetchAll(this.workspace.id)
        this.entries = data
      } catch (error) {
        notifyIf(error, 'workspace')
      }
    },
  },
}
</script>
